import { useI18n } from '@ecom/i18n';
import { EmptyState, type BadgeTone } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import type { OrdersByStatusEntry } from '@/features/analytics/types';
import { STATUS_LABEL_KEY, STATUS_TONE } from '@/features/orders/components/OrderStatusBadge';

/**
 * Static, fully-literal tone → fill-class lookup (never a templated
 * `` `bg-${tone}` `` string) — Tailwind's JIT content scanner only
 * generates classes it can see verbatim in source.
 */
const BAR_FILL_CLASSES: Record<BadgeTone, string> = {
  good: 'bg-good',
  warn: 'bg-warn',
  critical: 'bg-critical',
  info: 'bg-info',
  accent: 'bg-accent',
  neutral: 'bg-inkFaint',
};

/**
 * "Répartition commandes par statut" — horizontal progress bars, NOT a
 * donut (supervisor decision, Sprint UX/UI Merchant Dashboard V2: "Je ne
 * veux pas de donut"), styled after docs/reference/souk-dashboard.html's
 * own status-distribution block. Reused as-is by both `DashboardPage.tsx`
 * and `AnalyticsPage.tsx` so the two screens never show two different
 * visual languages for the same real data
 * (`MerchantAnalyticsOverview.ordersByStatus`).
 */
export function StatusBreakdownBars({
  entries,
}: {
  entries: readonly OrdersByStatusEntry[];
}): ReactElement {
  const { t } = useI18n();
  const total = entries.reduce((sum, entry) => sum + entry.count, 0);

  if (entries.length === 0 || total === 0) {
    return (
      <EmptyState
        title={t('merchant.common.noOrdersYet')}
        description={t('merchant.dashboard.statusBreakdown.emptyDescription')}
      />
    );
  }

  const sorted = [...entries].sort((a, b) => b.count - a.count);

  return (
    <div className="flex flex-col gap-3">
      {sorted.map((entry) => {
        const percent = (entry.count / total) * 100;
        return (
          <div key={entry.status}>
            <div className="mb-1 flex items-center justify-between text-xs font-semibold">
              <span className="text-ink">{t(STATUS_LABEL_KEY[entry.status])}</span>
              <span className="font-mono tabular-nums text-inkSoft">{entry.count}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-surface2">
              <div
                className={`h-full rounded-full ${BAR_FILL_CLASSES[STATUS_TONE[entry.status]]}`}
                style={{ width: `${String(percent)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
